import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Shield, Database, Terminal, Network, Zap, Search } from "lucide-react";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminToolkitPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication
  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
        return;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toolkitOptions = [
    {
      title: "Hunting Toolkit",
      description: "Manage pentest toolkits with groups, sections, and commands",
      icon: <Shield className="w-8 h-8" />,
      route: "/aAyuiOcvMq/hunting-toolkit",
      color: "bg-blue-500 hover:bg-blue-600"
    },
    {
      title: "LOLBAS",
      description: "Manage Living Off The Land Binaries and Scripts integration",
      icon: <Database className="w-8 h-8" />,
      route: "/aAyuiOcvMq/lolbas",
      color: "bg-green-500 hover:bg-green-600"
    },
    {
      title: "GTFOBins",
      description: "Manage Unix binaries for bypassing local security restrictions",
      icon: <Terminal className="w-8 h-8" />,
      route: "/aAyuiOcvMq/gtfobins",
      color: "bg-purple-500 hover:bg-purple-600"
    },
    {
      title: "WADComs",
      description: "Manage Windows Active Directory commands and techniques",
      icon: <Network className="w-8 h-8" />,
      route: "/aAyuiOcvMq/wadcoms",
      color: "bg-orange-500 hover:bg-orange-600"
    },
    {
      title: "Reverse Shell Generator",
      description: "Manage reverse shell payload generator",
      icon: <Zap className="w-8 h-8" />,
      route: "/aAyuiOcvMq/reverse-shell",
      color: "bg-red-500 hover:bg-red-600"
    },
    {
      title: "OSINT Toolkit",
      description: "Manage Open-Source Intelligence tools and reconnaissance techniques",
      icon: <Search className="w-8 h-8" />,
      route: "/aAyuiOcvMq/osint",
      color: "bg-indigo-500 hover:bg-indigo-600"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/admin')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/aAyuiOcvMq/admin')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Admin Toolkits
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Manage different types of toolkits and resources
            </p>
          </div>

          {/* Toolkit Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {toolkitOptions.map((option) => (
              <Card key={option.title} className="border-2 border-[#E7E7E7] hover:shadow-lg transition-shadow cursor-pointer" 
                    onClick={() => navigate(option.route)}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg text-white ${option.color}`}>
                      {option.icon}
                    </div>
                    <CardTitle className="text-xl text-black">{option.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">{option.description}</p>
                  <Button 
                    className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(option.route);
                    }}
                  >
                    Manage {option.title}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-['Space_Grotesk'] font-semibold text-black mb-2">About Toolkits</h3>
            <p className="text-sm text-gray-600">
              Toolkits provide organized collections of tools, commands, and resources for different aspects of cybersecurity testing. 
              Each toolkit type serves a specific purpose and can be managed independently.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 